import type { PortalBrandTheme } from '../../types/portalReport.js';

// CLIENT PORTAL Phase 2 — the brand themes.
//
// ⚠️⚠️ ACCENTS ON KYCK CHROME, NOT A SKIN. See `PortalBrandTheme` for the whole
// reasoning; the short version is that these values reach FOUR surfaces — the
// header lockup, the active tab underline, the selected metric chip, and one
// accent rule in the page header — and nothing else. If you find yourself
// theming a table row or a card surface, that is the skin we decided against.
//
// ⚠️⚠️ AND NEVER A DATA COLOUR. Snap's accent is red; a red performance chart
// reads as loss. `ReportChart`'s coverage fills and `MultiTrendChart`'s series
// colours are fixed across every brand and are pinned by tests.

/**
 * Snap Fitness.
 *
 * Verified twice — from the brand deck's vector fills, and against `--j-red` in
 * Scott's Snap proposal.
 *
 * ⚠️ SHELBY IS DELIBERATELY EXCLUDED, and this is a licence position rather than
 * a taste one. It appears three times in the entire proposal and earns nothing
 * on a page of numbers — but more importantly it is an ADOBE FONTS face: the
 * `.ttf` files we hold carry a Typekit EULA and self-hosting them for web is not
 * permitted. If it is ever wanted it goes through an Adobe Fonts web project
 * served from `use.typekit.net`, never a file under `public/fonts/`.
 */
const SNAP_FITNESS: PortalBrandTheme = {
  accent: '#C4161C',
  accentDark: '#9C1116',
  ink: '#141414',
  gradient: 'linear-gradient(90deg,#C4161C 0%,#C4161C 34%,#EE7623 100%)',
  logoUrl: '/brand/snap-fitness.webp',
  logoAlt: 'Snap Fitness',
  fontDisplay: "'Poppins', Geist, system-ui, sans-serif",
  fontBody: "'Poppins', Geist, system-ui, sans-serif",
  fontMono: "'Space Mono', 'Geist Mono', ui-monospace, monospace",
};

/**
 * Fitstop. All values verified against the Nov 2025 brand book's palette page.
 *
 * ⚠️ THE ORANGE IS THE BRAND SIGNAL, not the navy. The book itself calls
 * `#F15A22` "a vibrant accent colour used for call to actions", and Fitstop's
 * navy is so close to a neutral ink that using it as the accent would read as no
 * branding at all.
 *
 * ⚠️ NO `gradient` — the book does not define one, and inventing a plausible
 * gradient for someone else's brand is exactly the kind of quiet fabrication
 * that ends up in front of a client.
 *
 * ⚠️⚠️ THE ARTWORK IS THE WHITE MARK (2026-09-01), and it is white because the
 * report header is DARK IN BOTH THEMES: `.kyck-portal .rpt-top` is
 * `background: var(--rpt-ink-deep)` with no light-mode override. That one fact
 * is why this is a single `logoUrl` and not a light/dark pair — there is no
 * surface on this report that needs the black mark.
 *
 * ⚠️ VERIFIED BY INSPECTION, NOT BY FILENAME. A screenshot of a white logo on a
 * white background carries no information at all, so the artwork was decoded and
 * COMPOSITED ONTO #081113 — the real surface — and looked at: the FITSTOP
 * wordmark in white with the orange square as its full stop. Exactly two opaque
 * colours (#FFFFFF and #E24826), 1364 × 232, RGBA with a real alpha channel.
 * That technique generalises, and it is cheap; use it before trusting any mark.
 *
 * ⚠️ 1364 × 232 is 5.88:1 against the black mark's 5.84:1 (514 × 88) — a
 * geometric drop-in, so at the header's `height: 32px` it renders ~188px wide,
 * the same as before. No layout change.
 *
 * ⚠️ PNG, NOT WEBP, and deliberately: 4KB for a flat two-colour mark is already
 * smaller than the conversion is worth, and re-encoding a small mark risks edge
 * quality for nothing.
 *
 * ℹ️ The mark's orange is #E24826 while the palette accent above is #F15A22.
 * NOT a conflict — the restyled report emits no brand custom properties at all,
 * so #F15A22 never renders on this page. `logoUrl` and `logoAlt` are the only
 * brand-theme fields this report consumes (`restyleBrandScope.test.ts`).
 */
const FITSTOP: PortalBrandTheme = {
  accent: '#F15A22',
  ink: '#212532',
  logoUrl: '/brand/fitstop-white.png',
  logoAlt: 'Fitstop',
  fontDisplay: "'GT America Compressed', Geist, system-ui, sans-serif",
  fontBody: "'Aktiv Grotesk', Geist, system-ui, sans-serif",
  fontMono: "'GT America Mono', 'Geist Mono', ui-monospace, monospace",
};

/**
 * Anytime Fitness. ONE theme, BOTH markets (§7, 2026-09-04).
 *
 * ⚠️ AU AND NZ ARE TWO BRAND ACCOUNTS AND ONE BRAND, so they resolve to this
 * single entry rather than two near-identical ones that would drift the first
 * time somebody edited only the one they were looking at. `brandSlugFromName`
 * matches the leading token, which covers both.
 *
 * ⚠️⚠️ THE ARTWORK IS VERIFIED BY INSPECTION, NOT BY FILENAME — the technique the
 * Fitstop note prescribes, because a screenshot of a white logo on a white
 * background carries no information at all. Decoded and COMPOSITED ONTO #081113,
 * the real header surface, and looked at: the stylised AF monogram, 647 × 253,
 * RGBA with a real alpha channel (51% fully transparent), and EXACTLY ONE opaque
 * colour — #FFFFFF. No stray off-white, no baked-in background.
 *
 * ⚠️ WHITE IS RIGHT, AND THAT IS CHECKED RATHER THAN ASSUMED: `.psh-top` is
 * `background: var(--rpt-ink-deep)` OUTSIDE any mode block (index.css), so the
 * shell header is ink-deep in BOTH light and dark. There is no surface on this
 * report that would need a black mark, which is why this is a single `logoUrl`
 * and not a light/dark pair.
 *
 * ⚠️ 2.56:1 against Fitstop's 5.88:1 — a monogram is squarer than a wordmark, so
 * it renders NARROWER, not wrong. MEASURED in the shell header: 72 × 28 CSS px,
 * against Fitstop's ~188px wide. Nothing to correct.
 *
 * ⚠️⚠️ MONOGRAM ALONE, NO WORDMARK (Scott) — which makes `logoAlt` THE ONLY PLACE
 * THE CLIENT'S NAME APPEARS IN THE HEADER. So it reads exactly `Anytime
 * Fitness`: it is doing the naming, not describing an image. Not "monogram",
 * not "logo".
 *
 * ⚠️⚠️ `accent` AND `ink` ARE REQUIRED FIELDS HELD ALIVE ONLY BY DEAD CODE, and
 * that is worth recording rather than quietly satisfying. Nothing a client can
 * see reads either: the restyled report emits no brand custom properties at all,
 * its lockup rule and active tab underline are KYCK'S GREEN, and the sole
 * consumer of `--portal-accent` is `ChannelBlock`, which sits below a branch that
 * is unreachable for a portal user since Phase 1 of the old report's retirement.
 *
 * ⚠️ `accent` IS SCOTT'S OWN VALUE, supplied rather than eyeballed off artwork, so
 * it costs nothing to hold and involves no fabrication.
 *
 * ⚠️⚠️ `ink` IS NOT A CLAIM ABOUT THIS BRAND. We have no verified Anytime near-
 * black, and inventing a plausible one for someone else's brand is exactly the
 * quiet fabrication the Fitstop note refuses over its missing gradient. It holds
 * KYCK'S OWN #0f172a — the same value `--portal-accent` already defaults to in
 * index.css — so in the event it ever did render, it would render what an
 * UNTHEMED page renders rather than a colour we made up. Replace it if a brand
 * book turns up; do not treat it as evidence of one.
 *
 * ⚠️ NO FONTS, and that is the current design rather than a gap: they are
 * optional and absent means kyck's Geist, which is what the restyled report uses
 * for every brand (Scott, 2026-08-30, having seen the brand-font version live).
 * Declaring a face we have not licensed would also be a licence position taken
 * by accident.
 *
 * ⚠️ NO `gradient` and no `accentDark`: neither is defined for this brand and
 * neither renders. `accentDark` would otherwise fall back to the accent anyway.
 */
const ANYTIME_FITNESS: PortalBrandTheme = {
  accent: '#6245bb',
  ink: '#0f172a',
  logoUrl: '/brand/anytime-fitness-white.png',
  logoAlt: 'Anytime Fitness',
};

/**
 * Jetts Australia.
 *
 * ⚠️⚠️ THE ARTWORK IS VERIFIED BY INSPECTION, NOT BY FILENAME — the technique the
 * Fitstop note prescribes, because a screenshot of a white logo on a white
 * background carries no information at all. Decoded and COMPOSITED ONTO #081113,
 * the real header surface, and looked at: the italic Jetts WORDMARK, 594 × 239,
 * RGBA with a real alpha channel (59% fully transparent), and EXACTLY ONE opaque
 * colour — #FFFFFF. No stray off-white, no baked-in background.
 *
 * ⚠️ WHITE IS RIGHT, and checked rather than assumed: `.psh-top` is
 * `background: var(--rpt-ink-deep)` OUTSIDE any mode block (index.css), so the
 * shell header is ink-deep in BOTH light and dark. There is no surface on this
 * report that would need a black mark, which is why this is a single `logoUrl`
 * and not a light/dark pair.
 *
 * ⚠️ 2.49:1, against Fitstop's 5.88:1 and the Anytime monogram's 2.56:1 — a
 * COMPACT WORDMARK rather than a long one, so it renders ~80px wide at the
 * header's 32px. Narrower than Fitstop's ~188px, and nothing to correct.
 *
 * ⚠️ Unlike the Anytime entry, the mark READS "Jetts", so the wordmark does the
 * naming and `logoAlt` is ordinary alt text rather than the only place the
 * client's name appears on screen.
 *
 * ⚠️⚠️ `accent` AND `ink` ARE BOTH KYCK'S OWN #0f172a, AND THAT IS NOT A CLAIM
 * ABOUT THIS BRAND. We hold no verified Jetts colour of any kind, and inventing
 * a plausible one for someone else's brand is exactly the quiet fabrication the
 * Fitstop note refuses over its missing gradient. #0f172a is the value
 * `--portal-accent` and `--portal-ink` ALREADY DEFAULT TO in index.css, so in
 * the event either ever did render, it would render what an UNTHEMED page
 * renders rather than a colour we made up.
 *
 * ⚠️ THIS GOES ONE STEP FURTHER THAN THE ANYTIME ENTRY, which parks only `ink`
 * that way — its `accent` is Scott's own supplied value. Here there is no
 * supplied value to hold, so BOTH fields are placeholders. Replace them if a
 * brand book turns up; do not read them as evidence of one.
 *
 * ⚠️ Neither renders today: the restyled report emits no brand custom properties
 * at all, and `logoUrl`/`logoAlt` are the only fields it consumes
 * (`restyleBrandScope.test.ts`). They are REQUIRED fields kept alive by dead code.
 *
 * ⚠️ NO FONTS, and that is the current design rather than a gap: absent means
 * kyck's Geist, which is what the restyled report uses for every brand (Scott,
 * 2026-08-30). Declaring a face we have not licensed would also be a licence
 * position taken by accident.
 *
 * ⚠️ NO `gradient` and no `accentDark`: neither is defined for this brand and
 * neither renders. `accentDark` would otherwise fall back to the accent anyway.
 */
const JETTS: PortalBrandTheme = {
  accent: '#0f172a',
  ink: '#0f172a',
  logoUrl: '/brand/jetts-white.png',
  logoAlt: 'Jetts',
};

/**
 * O-Studio.
 *
 * ⚠️ ARTWORK IS THE WHITE MARK DESPITE THE FILENAME (2026-09-25). `o-studio.png`
 * carries no `-white` suffix but IS white — verified by compositing onto
 * #081113, the real header surface, not by reading the name. See FITSTOP above
 * for why a single `logoUrl` is right: this report has no surface needing a
 * black mark. ⚠️ Do not rename it to `-white`: it is the file Scott placed, and
 * a rename buys a broken path for a cosmetic gain.
 *
 * ⚠️ Colours from the O-Studio brand system (forest green primary, warm gold
 * accent) — the `ostudio-landing-page` skill's brand reference. NOT a placeholder
 * pair — contrast with JETTS above. ⚠️ They do not render: the restyled report
 * consumes only `logoUrl`/`logoAlt` (see JETTS).
 *
 * ⚠️ NO `gradient`: the brand system defines none, and inventing a plausible one
 * for someone else's brand is the fabrication the FITSTOP note refuses.
 *
 * ⚠️ NO FONTS — absent means Kyck's Geist, which is what the restyled report
 * uses for every brand (Scott, 2026-08-30).
 */
const O_STUDIO: PortalBrandTheme = {
  accent: '#3E6152',
  accentDark: '#2C463B',
  ink: '#263A31',
  logoUrl: '/brand/o-studio.png',
  logoAlt: 'O-Studio',
};
/**
 * The themes, by a stable slug.
 *
 * ⚠️ Keyed by SLUG rather than by brand account id because a brand account id is
 * a Firestore auto-id that only exists in the live database. The id → slug
 * binding lives in `config.ts`, which is the one place that needs real ids.
 */
export const PORTAL_BRAND_THEMES = {
  'snap-fitness': SNAP_FITNESS,
  fitstop: FITSTOP,
  'anytime-fitness': ANYTIME_FITNESS,
  jetts: JETTS,
  'o-studio': O_STUDIO,
} as const satisfies Record<string, PortalBrandTheme>;

export type PortalBrandSlug = keyof typeof PORTAL_BRAND_THEMES;

/**
 * Match a brand ACCOUNT NAME to a theme slug.
 *
 * ⚠️⚠️ THIS IS A FALLBACK, NOT THE PRIMARY BINDING, and it exists for one honest
 * reason: brand account ids are Firestore auto-ids, so they cannot be committed
 * from outside the live database, and a Phase 2 that shipped with an empty
 * registry would be a branding feature that brands nothing. Name matching gets
 * Snap and Fitstop themed today; `BRAND_ACCOUNT_THEMES` in config.ts takes
 * PRECEDENCE the moment the real ids are filled in, and this can then go.
 *
 * ⚠️ Deliberately CONSERVATIVE. It matches on a distinctive leading token, so
 * "Snap Fitness Australia" and "Snap Fitness New Zealand" both resolve to snap,
 * while an unrelated brand resolves to nothing rather than to the nearest guess.
 * A wrong theme is only cosmetic, but it is a client-visible wrong, so the rule
 * fails to NO THEME rather than to a plausible one.
 */
export function brandSlugFromName(brandName: string | null): PortalBrandSlug | null {
  if (!brandName) return null;
  const n = brandName.trim().toLowerCase();
  if (!n) return null;
  if (n.startsWith('snap fitness')) return 'snap-fitness';
  if (n.startsWith('fitstop')) return 'fitstop';
  // ⚠️ ONE RULE FOR BOTH MARKETS: "Anytime Fitness Australia" and "Anytime Fitness
  // New Zealand" are two brand ACCOUNTS and one BRAND, and a leading-token match
  // covers both without a second entry to keep in step.
  if (n.startsWith('anytime fitness')) return 'anytime-fitness';
  // ⚠️ TWO SPELLINGS, ONE BRAND. The account name has not been confirmed —
  // "O-Studio Global" appears in a comment in googleAdsReporting.ts but that is
  // an ILLUSTRATIVE EXAMPLE, not data, and must not be treated as the name.
  // Matching both the hyphen and the space covers whichever it is. ⚠️ Still a
  // LEADING token: never a bare "o" or a substring match.
  if (n.startsWith('o-studio') || n.startsWith('o studio')) return 'o-studio';
  return null;
}
